import { ThemeButton } from './ThemeWrapper';
import {
  PROGRAM_TYPES,
  PROGRAM_NAMES,
  USER_CATEGORIES,
  MODES,
  CENTERS,
} from '../domain/pma/pmaIdentityPolicy';

const fieldStyle = {
  borderColor: 'var(--color-border)',
  backgroundColor: 'var(--color-background)',
};

function Field({ label, children }) {
  return (
    <label className="text-sm theme-text">
      {label}
      {children}
    </label>
  );
}

function inputClass() {
  return 'block w-full mt-1 px-2 py-2 rounded border theme-text';
}

export default function ReservedUnixStudentsPanel({
  form,
  setField,
  presetId,
  applyPreset,
  presets,
  summary,
  preview,
  result,
  error,
  previewing,
  importing,
  onPreview,
  onImport,
}) {
  const busy = previewing || importing;

  return (
    <div className="profile-card theme-animate p-6 space-y-4 mt-6">
      <div>
        <h2 className="text-lg font-semibold theme-text">Reserve unix students for a group</h2>
        <p className="text-sm theme-text-secondary mt-1">
          Create SMS logins and reserve unix usernames now. Linux / VNC accounts are not created —
          add those later with the same user-ids. Use a saved institution or fill any group.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Institution preset">
          <select
            className={inputClass()}
            style={fieldStyle}
            value={presetId}
            onChange={(e) => applyPreset(e.target.value)}
          >
            <option value="">Custom group</option>
            {presets.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {preset.org || preset.id}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Institution / group">
          <input
            className={inputClass()}
            style={fieldStyle}
            value={form.org}
            placeholder="e.g. IIMT"
            onChange={(e) => setField('org', e.target.value)}
          />
        </Field>
        <Field label="Slug">
          <input
            className={inputClass()}
            style={fieldStyle}
            value={form.slug}
            placeholder="iimt"
            onChange={(e) => setField('slug', e.target.value)}
          />
        </Field>
        <Field label="Unix prefix">
          <input
            className={inputClass()}
            style={fieldStyle}
            value={form.prefix}
            placeholder="ws_iimtuser"
            onChange={(e) => setField('prefix', e.target.value)}
          />
        </Field>
        <Field label="Count">
          <input
            className={inputClass()}
            style={fieldStyle}
            type="number"
            min={1}
            max={200}
            value={form.count}
            onChange={(e) => setField('count', e.target.value === '' ? '' : Number(e.target.value))}
          />
        </Field>
        <Field label="Start at">
          <input
            className={inputClass()}
            style={fieldStyle}
            type="number"
            min={1}
            value={form.start}
            onChange={(e) => setField('start', e.target.value === '' ? '' : Number(e.target.value))}
          />
        </Field>
        <Field label="Email domain">
          <input
            className={inputClass()}
            style={fieldStyle}
            value={form.emailDomain}
            placeholder="iimt.example.com"
            onChange={(e) => setField('emailDomain', e.target.value)}
          />
        </Field>
        <Field label="program_type">
          <select
            className={inputClass()}
            style={fieldStyle}
            value={form.programType}
            onChange={(e) => setField('programType', e.target.value)}
          >
            {PROGRAM_TYPES.map((type) => (
              <option key={type} value={type}>
                {type} — {PROGRAM_NAMES[type] || type}
              </option>
            ))}
          </select>
        </Field>
        <Field label="user_category">
          <select
            className={inputClass()}
            style={fieldStyle}
            value={form.userCategory}
            onChange={(e) => setField('userCategory', e.target.value)}
          >
            {USER_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </Field>
        <Field label="mode">
          <select
            className={inputClass()}
            style={fieldStyle}
            value={form.mode}
            onChange={(e) => setField('mode', e.target.value)}
          >
            {MODES.map((mode) => (
              <option key={mode} value={mode}>
                {mode}
              </option>
            ))}
          </select>
        </Field>
        <Field label={form.mode === 'Online' ? 'Center (ignored for Online)' : 'Center'}>
          <select
            className={inputClass()}
            style={fieldStyle}
            value={form.center}
            disabled={form.mode === 'Online'}
            onChange={(e) => setField('center', e.target.value)}
          >
            <option value="">Select center</option>
            {CENTERS.map((center) => (
              <option key={center} value={center}>
                {center}
              </option>
            ))}
          </select>
        </Field>
        <Field label="linux-group (home /ve/{group}_home)">
          <input
            className={inputClass()}
            style={fieldStyle}
            value={form.linuxGroup}
            placeholder="ws"
            onChange={(e) => setField('linuxGroup', e.target.value)}
          />
        </Field>
        <Field label="Duration">
          <input
            className={inputClass()}
            style={fieldStyle}
            value={form.duration}
            placeholder="15d"
            onChange={(e) => setField('duration', e.target.value)}
          />
        </Field>
      </div>

      {summary.firstUnix ? (
        <p className="text-sm theme-text-secondary">
          Will reserve <strong>{summary.count}</strong> {summary.programName || 'workshop'} student
          {summary.count === 1 ? '' : 's'}
          {summary.org ? ` for ${summary.org}` : ''}:{' '}
          <code>{summary.firstUnix}</code>
          {summary.count > 1 ? (
            <>
              {' '}
              … <code>{summary.lastUnix}</code>
            </>
          ) : null}
          {summary.firstEmail ? (
            <>
              {' '}
              · {summary.firstEmail}
              {summary.count > 1 ? ` … ${summary.lastEmail}` : ''}
            </>
          ) : null}
        </p>
      ) : (
        <p className="text-sm theme-text-secondary">
          Enter an institution name to preview unix ids (for example IIMT → ws_iimtuser1).
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <ThemeButton type="button" variant="secondary" onClick={onPreview} disabled={busy || !form.org}>
          {previewing ? 'Checking…' : 'Preview collisions'}
        </ThemeButton>
        <ThemeButton type="button" onClick={onImport} disabled={busy || !form.org}>
          {importing ? 'Creating students…' : 'Create SMS students (no Linux)'}
        </ThemeButton>
      </div>

      {error && (
        <div
          className="p-3 rounded-lg text-sm whitespace-pre-wrap"
          style={{ backgroundColor: 'var(--color-error-100)', color: 'var(--color-error)' }}
        >
          {error}
        </div>
      )}

      {preview?.unixRange && (
        <div className="text-sm theme-text space-y-1">
          <p>
            Checked {preview.count} user-id{preview.count === 1 ? '' : 's'}:{' '}
            <code>{preview.unixRange.first}</code> … <code>{preview.unixRange.last}</code>
          </p>
          {preview.collisions?.emailHits?.length ? (
            <p className="theme-text-secondary">
              {preview.collisions.emailHits.length} existing email
              {preview.collisions.emailHits.length === 1 ? '' : 's'} will be updated.
            </p>
          ) : (
            <p className="theme-text-secondary">No matching emails in SMS yet.</p>
          )}
          {preview.collisions?.blockingUnix?.length ? (
            <p style={{ color: 'var(--color-error)' }}>
              {preview.collisions.blockingUnix.length} unix username
              {preview.collisions.blockingUnix.length === 1 ? '' : 's'} already taken by another
              email — those rows will fail.
            </p>
          ) : null}
        </div>
      )}

      {result && (
        <div className="text-sm theme-text space-y-1">
          <p className="font-medium">{result.message}</p>
          <p className="theme-text-secondary">
            {result.successCount} imported · {result.errorCount} failed
          </p>
          {result.errors?.length > 0 && (
            <ul className="space-y-1" style={{ color: 'var(--color-error)' }}>
              {result.errors.map((item) => (
                <li key={`${item.rowNumber}-${item.email || 'row'}`}>
                  Row {item.rowNumber}
                  {item.email ? ` (${item.email})` : ''}: {item.error}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
